import {
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';

// Domain types use ISO-8601 strings for dates; Firestore stores Timestamp.
// These converters bridge the two so the app never juggles Timestamp objects in the UI.

function toISO(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(toISO);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, toISO(v)]));
  }
  return value;
}

function fromISO(value: unknown): unknown {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return Timestamp.fromDate(d);
  }
  if (Array.isArray(value)) return value.map(fromISO);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, fromISO(v)]));
  }
  return value;
}

// Generic converter: attaches doc id, converts Timestamp <-> ISO string.
export function converter<T extends { id: string }>(): FirestoreDataConverter<T> {
  return {
    toFirestore(model: T): DocumentData {
      const { id: _id, ...rest } = model;
      return fromISO(rest) as DocumentData;
    },
    fromFirestore(snapshot: QueryDocumentSnapshot): T {
      const data = toISO(snapshot.data()) as Record<string, unknown>;
      return { id: snapshot.id, ...data } as T;
    },
  };
}
